/**
 * 表計算ソフトや仕入先の注文書から**貼り付けた**テキストを、発注書に足せる形へ解く。
 *
 * 🔴 **列の順番には一切頼らない。** 「商品 価格」で貼る人も「価格 商品」で貼る人もいる。
 * 商品コードの列は**店のカタログと突合して**決める（実在する SKU/JAN が並ぶ列がそれ）＝
 * 順番も見出しも言語も関係ない、いちばん強い信号。
 *
 * 🔴 いちばん危ないのは**価格の列を数量と読み違えること**（¥1,200 を数量1,200 として
 * 発注してしまう）。数値なら何でも数量とみなす実装にはせず、通貨の印がある列は
 * 数量の候補から落とす。それでも決めきれないときは**推測せず画面に選ばせる**。
 */

/** 貼り付けを解いた表。行 × セル。 */
export type PastedGrid = string[][];

/** 数量として解釈できる最大値。これを超える値は数量ではなく金額を疑う。 */
const MAX_PLAUSIBLE_QTY = 100_000;

/** 通貨・金額の印。この印を持つ列は数量の候補から落とす。 */
const MONEY_MARKERS = /[¥$€£円]|\.\d{2}$/;

/** 見出し行で数量列を指す語（日英）。 */
const QTY_HEADERS = [
  "数量",
  "個数",
  "注文数",
  "発注数",
  "入数",
  "数",
  "qty",
  "quantity",
  "amount ordered",
  "order qty",
];

/** 見出し行で商品コード列を指す語（列判定の裏取りに使う）。 */
const CODE_HEADERS = ["sku", "jan", "ean", "barcode", "商品コード", "品番", "バーコード"];

/** 引用符を尊重して1行を分割する（`"1,200"` を1セルとして保つ）。 */
function splitRow(line: string, delimiter: string): string[] {
  const cells: string[] = [];
  let current = "";
  let quoted = false;

  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    if (char === '"') {
      // 連続する "" は引用符そのもの。
      if (quoted && line[i + 1] === '"') {
        current += '"';
        i += 1;
        continue;
      }
      quoted = !quoted;
      continue;
    }
    if (!quoted && char === delimiter) {
      cells.push(current);
      current = "";
      continue;
    }
    current += char;
  }
  cells.push(current);
  return cells.map((cell) => cell.trim());
}

/** その行に使われている区切り文字を選ぶ。タブ → カンマ → 連続空白の順。 */
function delimiterFor(line: string): string | null {
  if (line.includes("\t")) return "\t";
  if (line.includes(",")) return ",";
  return null;
}

/**
 * 貼り付けテキストを行 × セルへ解く。空行は落とす。
 * 区切りが見つからない行（1列だけ）はそのまま1セルの行になる＝コードだけを
 * 縦に貼った場合に対応する（数量は画面側で 1 に倒す）。
 */
export function parsePastedGrid(text: string): PastedGrid {
  const rows: PastedGrid = [];
  for (const rawLine of text.split(/\r?\n/)) {
    const line = rawLine.trim();
    if (line === "") continue;
    const delimiter = delimiterFor(line);
    const cells = delimiter
      ? splitRow(line, delimiter)
      : line.split(/\s{2,}/).map((cell) => cell.trim());
    if (cells.every((cell) => cell === "")) continue;
    rows.push(cells);
  }
  return rows;
}

/**
 * セルを数量として読む。全角数字・桁区切りは受けるが、**通貨の印がある値は拒否**する。
 * 読めなければ null（＝この列は数量ではないかもしれない、という情報になる）。
 */
export function parseQuantity(cell: string): number | null {
  const trimmed = cell.trim();
  if (trimmed === "") return null;
  if (MONEY_MARKERS.test(trimmed)) return null;

  const normalized = trimmed
    // 全角数字を半角へ。
    .replace(/[０-９]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xfee0))
    .replace(/,/g, "")
    .replace(/[個点本箱ケースpcs]/gi, "")
    .trim();

  if (!/^\d+$/.test(normalized)) return null;
  const value = Number(normalized);
  if (value <= 0 || value > MAX_PLAUSIBLE_QTY) return null;
  return value;
}

function matchesAny(cell: string, words: string[]): boolean {
  const lower = cell.trim().toLowerCase();
  if (lower === "") return false;
  return words.some((word) => lower.includes(word));
}

export interface PasteColumnDetection {
  /** 商品コードの列。カタログに1件も当たらなければ null。 */
  codeColumn: number | null;
  /** 数量の列。決められなければ null＝すべて 1 として扱う。 */
  qtyColumn: number | null;
  /** 先頭行を見出しとして落とすか。 */
  hasHeader: boolean;
  /** 数量になり得る列（画面が選び直させるための候補）。 */
  qtyCandidates: number[];
}

/** 列数（行ごとに違い得るので最大を取る）。 */
export function columnCount(grid: PastedGrid): number {
  return grid.reduce((max, row) => Math.max(max, row.length), 0);
}

/**
 * どの列が商品コードで、どの列が数量かを決める。
 *
 * `matchedCodes` は**サーバーが実在すると答えたコード**の集合（大小文字はそのまま）。
 * これがあるので、列の位置・見出しの有無・言語に依存せず商品コード列を特定できる。
 */
export function detectPasteColumns(
  grid: PastedGrid,
  matchedCodes: ReadonlySet<string>,
): PasteColumnDetection {
  const columns = columnCount(grid);
  if (columns === 0) {
    return { codeColumn: null, qtyColumn: null, hasHeader: false, qtyCandidates: [] };
  }

  // 先頭行にカタログ一致がなく、既知の見出し語を含むなら見出し行。
  const first = grid[0] ?? [];
  const firstRowHasMatch = first.some((cell) => matchedCodes.has(cell.trim()));
  const hasHeader =
    grid.length > 1 &&
    !firstRowHasMatch &&
    first.some((cell) => matchesAny(cell, [...QTY_HEADERS, ...CODE_HEADERS]));
  const body = hasHeader ? grid.slice(1) : grid;

  // 商品コード列＝カタログに当たったセルがいちばん多い列。
  let codeColumn: number | null = null;
  let bestHits = 0;
  for (let col = 0; col < columns; col += 1) {
    const hits = body.filter((row) => matchedCodes.has((row[col] ?? "").trim())).length;
    if (hits > bestHits) {
      bestHits = hits;
      codeColumn = col;
    }
  }

  // 数量列の候補＝コード列以外で、数量として読めるセルがある列。
  const qtyCandidates: number[] = [];
  for (let col = 0; col < columns; col += 1) {
    if (col === codeColumn) continue;
    const parsable = body.filter((row) => parseQuantity(row[col] ?? "") !== null).length;
    if (parsable > 0) qtyCandidates.push(col);
  }

  // 見出しがあれば見出しで決める＝いちばん確かな根拠。
  let qtyColumn: number | null = null;
  if (hasHeader) {
    qtyColumn =
      qtyCandidates.find((col) => matchesAny(first[col] ?? "", QTY_HEADERS)) ?? null;
  }
  // 見出しで決まらなければ、数量として読めたセルが最も多い列。
  // （通貨の印がある列は parseQuantity が弾くので、ここには残りにくい。）
  if (qtyColumn === null && qtyCandidates.length > 0) {
    qtyColumn = qtyCandidates.reduce((best, col) => {
      const score = body.filter((row) => parseQuantity(row[col] ?? "") !== null).length;
      const bestScore = body.filter(
        (row) => parseQuantity(row[best] ?? "") !== null,
      ).length;
      return score > bestScore ? col : best;
    }, qtyCandidates[0]);
  }

  return { codeColumn, qtyColumn, hasHeader, qtyCandidates };
}
